import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CandidateLayout } from '@/components/candidate/CandidateLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Copy, ExternalLink, Check, Info } from 'lucide-react';
import { SEOSettings as SEOSettingsType } from '@/types/seo.types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usePageMeta } from '@/hooks/usePageMeta';
import { OGImageUpload } from '@/components/admin/OGImageUpload';

export default function SEOSettings() {
  usePageMeta({
    title: "Configurações SEO - Data Hire Club",
    description: "Configurações de SEO",
    noindex: true,
  });

  const [settings, setSettings] = useState<SEOSettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const { toast } = useToast();

  const edgeFunctionSitemapUrl = `https://jdwcgbwcwkrrvaqtokju.supabase.co/functions/v1/generate-sitemap`;
  const edgeFunctionRobotsUrl = `https://jdwcgbwcwkrrvaqtokju.supabase.co/functions/v1/generate-robots`;
  
  const sitemapUrl = settings?.site_url ? `${settings.site_url}/sitemap.xml` : '';
  const robotsUrl = settings?.site_url ? `${settings.site_url}/robots.txt` : '';

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('seo_settings')
        .select('*')
        .maybeSingle();

      if (error) throw error;
      setSettings(data as SEOSettingsType);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Não foi possível carregar as configurações',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('seo_settings')
        .update({
          site_url: settings.site_url,
          sitemap_enabled: settings.sitemap_enabled,
          robots_txt_content: settings.robots_txt_content,
          default_meta_description: settings.default_meta_description,
          default_og_image: settings.default_og_image,
          google_search_console_verification: settings.google_search_console_verification,
          sitemap_frequency: settings.sitemap_frequency,
        })
        .eq('id', settings.id);

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: 'Configurações salvas com sucesso',
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
    toast({
      title: 'Copiado!',
      description: `${label} copiado para a área de transferência`,
    });
  };

  if (loading) {
    return (
      <CandidateLayout maxWidth="4xl">
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </CandidateLayout>
    );
  }

  if (!settings) {
    return (
      <CandidateLayout maxWidth="4xl">
        <p>Erro ao carregar configurações</p>
      </CandidateLayout>
    );
  }

  return (
    <CandidateLayout maxWidth="4xl">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Configurações de SEO</h1>
          <p className="text-muted-foreground mt-2">
            Gerencie sitemap, robots.txt e meta tags do site
          </p>
        </div>

        {/* Configuration Instructions */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="space-y-2">
            <p className="font-medium">Como configurar o Sitemap no seu domínio:</p>
            <ol className="list-decimal list-inside space-y-1 text-sm ml-2">
              <li>Configure um redirect no seu domínio/servidor:</li>
              <div className="bg-muted p-2 rounded text-xs font-mono ml-6 my-1">
                <div>/sitemap.xml → {edgeFunctionSitemapUrl}</div>
                <div>/robots.txt → {edgeFunctionRobotsUrl}</div>
              </div>
              <li>Após configurar, adicione no Google Search Console: <code className="bg-muted px-1 py-0.5 rounded text-xs">{sitemapUrl}</code></li>
            </ol>
          </AlertDescription>
        </Alert>

        {/* Sitemap Section */}
        <Card>
          <CardHeader>
            <CardTitle>Sitemap XML</CardTitle>
            <CardDescription>
              Sitemap automático gerado dinamicamente com páginas e vagas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Sitemap Ativo</Label>
                <p className="text-sm text-muted-foreground">
                  Habilitar geração automática do sitemap
                </p>
              </div>
              <Switch
                checked={settings.sitemap_enabled}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, sitemap_enabled: checked })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>URL do Sitemap (para Google Search Console)</Label>
              <div className="flex gap-2">
                <Input value={sitemapUrl} readOnly className="flex-1" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(sitemapUrl, 'URL do Sitemap')}
                  disabled={!sitemapUrl}
                >
                  {copied === 'URL do Sitemap' ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Configure a URL do site acima para ver a URL final do sitemap
              </p>
            </div>

            <div className="space-y-2">
              <Label>Edge Function URL (para configurar redirect)</Label>
              <div className="flex gap-2">
                <Input value={edgeFunctionSitemapUrl} readOnly className="flex-1" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(edgeFunctionSitemapUrl, 'Edge Function Sitemap')}
                >
                  {copied === 'Edge Function Sitemap' ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => window.open(edgeFunctionSitemapUrl, '_blank')}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Frequência de Atualização</Label>
              <Select
                value={settings.sitemap_frequency}
                onValueChange={(value: any) =>
                  setSettings({ ...settings, sitemap_frequency: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="always">Sempre</SelectItem>
                  <SelectItem value="hourly">A cada hora</SelectItem>
                  <SelectItem value="daily">Diariamente</SelectItem>
                  <SelectItem value="weekly">Semanalmente</SelectItem>
                  <SelectItem value="monthly">Mensalmente</SelectItem>
                  <SelectItem value="yearly">Anualmente</SelectItem>
                  <SelectItem value="never">Nunca</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Robots.txt Section */}
        <Card>
          <CardHeader>
            <CardTitle>Robots.txt</CardTitle>
            <CardDescription>
              Configure quais páginas os crawlers podem acessar
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>URL do Robots.txt (final)</Label>
              <div className="flex gap-2">
                <Input value={robotsUrl} readOnly className="flex-1" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(robotsUrl, 'URL do Robots.txt')}
                  disabled={!robotsUrl}
                >
                  {copied === 'URL do Robots.txt' ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Configure a URL do site acima para ver a URL final do robots.txt
              </p>
            </div>

            <div className="space-y-2">
              <Label>Edge Function URL (para configurar redirect)</Label>
              <div className="flex gap-2">
                <Input value={edgeFunctionRobotsUrl} readOnly className="flex-1" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(edgeFunctionRobotsUrl, 'Edge Function Robots')}
                >
                  {copied === 'Edge Function Robots' ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => window.open(edgeFunctionRobotsUrl, '_blank')}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Conteúdo do Robots.txt</Label>
              <Textarea
                value={settings.robots_txt_content}
                onChange={(e) =>
                  setSettings({ ...settings, robots_txt_content: e.target.value })
                }
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Use {`{{SITE_URL}}`} como placeholder para a URL do site
              </p>
            </div>
          </CardContent>
        </Card>

        {/* General Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Configurações Gerais</CardTitle>
            <CardDescription>Meta tags padrão e URL do site</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>URL do Site</Label>
              <Input
                value={settings.site_url}
                onChange={(e) => setSettings({ ...settings, site_url: e.target.value })}
                placeholder="https://seusite.com"
              />
            </div>

            <div className="space-y-2">
              <Label>Meta Description Padrão</Label>
              <Textarea
                value={settings.default_meta_description}
                onChange={(e) =>
                  setSettings({ ...settings, default_meta_description: e.target.value })
                }
                rows={3}
                maxLength={160}
              />
              <p className="text-xs text-muted-foreground">
                {settings.default_meta_description?.length || 0}/160 caracteres
              </p>
            </div>

            <div className="space-y-2">
              <Label>Imagem Open Graph Padrão</Label>
              <p className="text-xs text-muted-foreground">
                Esta imagem aparece quando você compartilha o link do site em redes sociais (WhatsApp, Facebook, LinkedIn, etc)
              </p>
              
              <OGImageUpload 
                currentImageUrl={settings.default_og_image || null}
                onImageChange={(url) => setSettings({ ...settings, default_og_image: url })}
              />

              <Alert className="mt-4">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium mb-2">📱 Como funciona a Imagem Open Graph:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Aparece em compartilhamentos no WhatsApp, Facebook, LinkedIn, Twitter/X</li>
                    <li>Dimensões recomendadas: <strong>1200x630 pixels</strong> (proporção 1.91:1)</li>
                    <li>Use imagens de alta qualidade com texto legível</li>
                    <li>Evite texto muito pequeno (pode ficar ilegível em dispositivos mobile)</li>
                    <li>Após fazer upload, clique em "Salvar Configurações" para aplicar</li>
                  </ul>
                </AlertDescription>
              </Alert>
              
              <details className="mt-4">
                <summary className="text-sm cursor-pointer text-muted-foreground hover:text-foreground">
                  Ou insira URL manualmente
                </summary>
                <Input
                  value={settings.default_og_image || ''}
                  onChange={(e) => setSettings({ ...settings, default_og_image: e.target.value })}
                  placeholder="https://seusite.com/og-image.jpg"
                  className="mt-2"
                />
              </details>
            </div>

            <div className="space-y-2">
              <Label>Google Search Console - Código de Verificação</Label>
              <Input
                value={settings.google_search_console_verification || ''}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    google_search_console_verification: e.target.value,
                  })
                }
                placeholder="google-site-verification=..."
              />
              <p className="text-xs text-muted-foreground">
                Cole o código de verificação do Google Search Console
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Google Search Console Instructions */}
        <Card>
          <CardHeader>
            <CardTitle>Como Configurar no Google Search Console</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>
                Acesse{' '}
                <a
                  href="https://search.google.com/search-console"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Google Search Console
                </a>
              </li>
              <li>Adicione sua propriedade (URL do site)</li>
              <li>Escolha o método de verificação "Tag HTML"</li>
              <li>Cole o código de verificação no campo acima</li>
              <li>Clique em "Salvar" e depois "Verificar" no Google</li>
            </ol>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Salvar Configurações
          </Button>
        </div>
      </div>
    </CandidateLayout>
  );
}
